/**
 * GoogleAdsCreativesPanel
 *
 * A launched Google Ads campaign's creative on the review pane — per ad group /
 * asset group: images, videos, headlines, descriptions, final URL, sitelinks and
 * callouts. The review grid's Creative tab is built from the launch/import PLAN,
 * which for a Search / PMax / Demand Gen campaign carries no creative at all, so
 * that tab was a row of '—'. These come from the grid's own setup pull instead
 * (GET /api/v2/campaign-grid/:id/creatives), the same rows the Campaign Grid
 * renders its thumbnails from.
 *
 * `groups` is null for a non-Google campaign (renders nothing), [] when the grid
 * has not pulled the campaign yet.
 */
import React, { useState } from 'react';

// Groups shown before "Show all": an imported PMax campaign can have 80+.
const GROUPS_SHOWN = 4;
// Images per group before "+N more".
const IMAGES_SHOWN = 12;

const isHttp = (u) => /^https?:\/\//i.test(String(u || ''));

function ExternalLink({ href, children, className }) {
  if (!isHttp(href)) return <span className={className}>{children}</span>;
  return <a className={className} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function StatusPill({ status }) {
  if (!status) return null;
  const s = String(status).toUpperCase();
  return <span className={`clw-cr-status ${s.toLowerCase()}`}>{s.charAt(0) + s.slice(1).toLowerCase()}</span>;
}

function FinalUrl({ urls, path1, path2 }) {
  const url = (urls || [])[0];
  if (!url) return null;
  const display = [path1, path2].filter(Boolean).join('/');
  return (
    <div className="clw-cr-row">
      <span className="clw-cr-lbl">Final URL</span>
      <span className="clw-cr-val">
        <ExternalLink href={url} className="clw-cr-url">{url}</ExternalLink>
        {display && <span className="clw-cr-path"> · display path /{display}</span>}
        {urls.length > 1 && <span className="clw-cr-path"> · +{urls.length - 1} more</span>}
      </span>
    </div>
  );
}

function TextList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="clw-cr-row">
      <span className="clw-cr-lbl">{label} <span className="clw-cr-count">{items.length}</span></span>
      <ul className="clw-cr-texts">
        {items.map((t, i) => <li key={`${i}-${t}`}>{t}</li>)}
      </ul>
    </div>
  );
}

function Media({ images, videos }) {
  const [showAll, setShowAll] = useState(false);
  const imgs = images || [];
  const vids = videos || [];
  if (imgs.length === 0 && vids.length === 0) return null;
  const shownImgs = showAll ? imgs : imgs.slice(0, IMAGES_SHOWN);
  return (
    <div className="clw-cr-row">
      <span className="clw-cr-lbl">
        Media <span className="clw-cr-count">
          {[imgs.length ? `${imgs.length} image${imgs.length === 1 ? '' : 's'}` : null,
            vids.length ? `${vids.length} video${vids.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      <div className="clw-cr-media">
        {vids.map((v) => (
          <a
            key={`v-${v.youtube_id}`}
            className="clw-cr-thumb video"
            href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.youtube_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            title={v.title || 'YouTube video'}
          >
            <img src={`https://i.ytimg.com/vi/${encodeURIComponent(v.youtube_id)}/mqdefault.jpg`} alt={v.title || 'YouTube video'} loading="lazy" />
            <span className="clw-cr-play" aria-hidden="true">▶</span>
          </a>
        ))}
        {shownImgs.map((im) => (
          <ExternalLink key={`i-${im.url}`} href={im.url} className="clw-cr-thumb">
            <img src={isHttp(im.url) ? im.url : undefined} alt={im.label || 'Image asset'} title={im.label || undefined} loading="lazy" />
          </ExternalLink>
        ))}
        {imgs.length > IMAGES_SHOWN && (
          <button type="button" className="clw-cr-more" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : `+${imgs.length - IMAGES_SHOWN} more`}
          </button>
        )}
      </div>
    </div>
  );
}

function Extensions({ sitelinks, callouts }) {
  return (
    <>
      {sitelinks && sitelinks.length > 0 && (
        <div className="clw-cr-row">
          <span className="clw-cr-lbl">Sitelinks <span className="clw-cr-count">{sitelinks.length}</span></span>
          <div className="clw-cr-chips">
            {sitelinks.map((s, i) => (
              <ExternalLink key={`${i}-${s.text}`} href={s.url} className="clw-cr-chip">
                <strong>{s.text}</strong>
                {s.description && <span className="clw-cr-chip-sub">{s.description}</span>}
              </ExternalLink>
            ))}
          </div>
        </div>
      )}
      {callouts && callouts.length > 0 && (
        <div className="clw-cr-row">
          <span className="clw-cr-lbl">Callouts <span className="clw-cr-count">{callouts.length}</span></span>
          <div className="clw-cr-chips">
            {callouts.map((c, i) => <span key={`${i}-${c}`} className="clw-cr-chip">{c}</span>)}
          </div>
        </div>
      )}
    </>
  );
}

/** One unit of creative — an asset group, or one ad of an ad group. */
function CreativeBody({ unit }) {
  return (
    <>
      <Media images={unit.images} videos={unit.videos} />
      <TextList label="Headlines" items={unit.headlines} />
      <TextList label="Long headlines" items={unit.long_headlines} />
      <TextList label="Descriptions" items={unit.descriptions} />
      {unit.business_name && (
        <div className="clw-cr-row">
          <span className="clw-cr-lbl">Business name</span>
          <span className="clw-cr-val">{unit.business_name}</span>
        </div>
      )}
      <FinalUrl urls={unit.final_urls} path1={unit.path1} path2={unit.path2} />
    </>
  );
}

function GroupCard({ group }) {
  const isAssetGroup = group.kind === 'asset_group';
  return (
    <div className="clw-cr-group" data-testid="clw-creative-group">
      <div className="clw-cr-group-head">
        <span className="clw-cr-group-kind">{isAssetGroup ? 'Asset group' : 'Ad group'}</span>
        <span className="clw-cr-group-name">{group.name}</span>
        <StatusPill status={group.status} />
      </div>
      <div className="clw-cr-group-body">
        {isAssetGroup ? (
          <CreativeBody unit={group} />
        ) : group.ads.length === 0 ? (
          <div className="clw-cr-empty">No ads in this ad group.</div>
        ) : (
          group.ads.map((ad) => (
            <div key={ad.id} className="clw-cr-ad">
              <div className="clw-cr-ad-head">
                {ad.type || 'Ad'} <span className="clw-cr-ad-id">#{ad.id}</span>
                <StatusPill status={ad.status} />
              </div>
              <CreativeBody unit={ad} />
            </div>
          ))
        )}
        <Extensions sitelinks={group.sitelinks} callouts={group.callouts} />
      </div>
    </div>
  );
}

export function creativeTotals(groups) {
  let images = 0;
  let videos = 0;
  let ads = 0;
  for (const g of groups || []) {
    images += (g.images || []).length;
    videos += (g.videos || []).length;
    for (const ad of g.ads || []) {
      ads += 1;
      images += (ad.images || []).length;
      videos += (ad.videos || []).length;
    }
  }
  return { images, videos, ads };
}

export function GoogleAdsCreativesPanel({ groups, loading, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  if (groups == null && !loading) return null;

  const list = groups || [];
  const assetGroups = list.filter((g) => g.kind === 'asset_group').length;
  const adGroups = list.length - assetGroups;
  const totals = creativeTotals(list);
  const summary = [
    assetGroups ? `${assetGroups} asset group${assetGroups === 1 ? '' : 's'}` : null,
    adGroups ? `${adGroups} ad group${adGroups === 1 ? '' : 's'}` : null,
    totals.ads ? `${totals.ads} ad${totals.ads === 1 ? '' : 's'}` : null,
    totals.images ? `${totals.images} image${totals.images === 1 ? '' : 's'}` : null,
    totals.videos ? `${totals.videos} video${totals.videos === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
  const shown = showAll ? list : list.slice(0, GROUPS_SHOWN);

  return (
    <div className="clw-cr" data-testid="clw-creatives-panel">
      <button type="button" className="clw-cr-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>Creatives</span>
        {summary && <span className="clw-cr-head-sub">{summary}</span>}
        <span className="clw-cr-head-toggle">{open ? 'Hide ▲' : 'Show ▼'}</span>
      </button>
      {open && (
        <div className="clw-cr-body">
          {loading && list.length === 0 ? (
            <div className="clw-cr-empty">Loading creatives…</div>
          ) : list.length === 0 ? (
            <div className="clw-cr-empty">
              No creative pulled yet. It appears after the Campaign Grid&apos;s next pull for this campaign.
            </div>
          ) : (
            <>
              {shown.map((g) => <GroupCard key={g.id} group={g} />)}
              {list.length > GROUPS_SHOWN && (
                <button type="button" className="clw-cr-more wide" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Show fewer' : `Show all ${list.length} groups`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default GoogleAdsCreativesPanel;
